import { isLogoBackground, type LogoBackground } from "@pinksy/contracts";
import { authFetch, type FetchLike } from "@/auth/http";
import { API_BASE_URL } from "@/api/base";

/**
 * 🔴 **WHICH COMPANY YOU ARE ACTING FOR** — the organisation switcher's data (D37, D41, D42).
 *
 * This is the ONE endpoint that runs without an organisation, and it has to: a person who has just
 * signed in has not chosen one, and this is the list they choose from. It is scoped by the only thing
 * available — the authenticated user — and says nothing about any company they are not in.
 *
 * The shell asks, not a module, for the same reason it asks for a production's title: the switcher is
 * drawn at the top of the sidebar on every route, including ones whose module has never heard of an
 * organisation. And it asks with the held session, so a pasted link into a fresh tab works.
 *
 * Best-effort, like `production/productionApi.ts`: a 401 before sign-in, an offline first paint or a
 * server that is down each answer "no organisations known yet". None of them may turn a working
 * navigation into a failure.
 */
export const MY_ORGANISATIONS_PATH = "/me/organisations";

/**
 * 🔴 **THE SHELL WILL NOT WAIT FOREVER FOR THIS.**
 *
 * The Desk does not start its modules until the organisation question has been ANSWERED (boot.ts) —
 * which is what stops a module fetching before it has an `X-Organisation` header to send. The cost of
 * that gate is that a request which never settles would leave a blank Desk, permanently, with nothing
 * on screen to explain it.
 *
 * So the ask is bounded. A timeout is treated exactly like a failure: no organisations known, the
 * switcher says "No organisation", and a reload tries again. That is legible and recoverable, where a
 * blank screen is neither.
 *
 * Six seconds because this is one small query on the critical path of every sign-in: long enough to
 * ride out a slow first connection, short enough that a person has not yet decided the app is broken.
 */
export const ORGANISATIONS_TIMEOUT_MS = 6000;

/**
 * One row in the switcher — `services/user/my-organisations.ts`.
 *
 * The shell declares the fields it reads rather than importing the Encore app's types: an app never
 * imports an Encore app's sources (CLAUDE.md).
 */
export interface OrganisationChoice {
  /** The public identifier. This is what goes back as `X-Organisation` on every request (D39). */
  id: string;
  /** The trading name where there is one, else the legal name — what the person calls the company. */
  name: string;
  /** The registered name, shown under the trading one when they differ. */
  legalName: string;
  /** What this person IS to it (D22) — principal · employee · contractor · agent · booker. */
  relationship: string;
  /** Their job title on the edge. Free text, often absent. */
  position: string | null;
  /** ACTIVE · SUSPENDED · BANNED · CLOSED. */
  standing: string;
  /**
   * 🔴 False when the standing blocks it — and the row is still RETURNED.
   *
   * A company a person knows they work at, silently missing from their own list, reads as the platform
   * having lost it. Shown and greyed says the true thing: it is there, and you cannot enter it today.
   * The gate refuses the request either way (layer 3); this only decides what the switcher draws.
   */
  selectable: boolean;
  /**
   * 🔴 **THIS COMPANY REPRESENTS THE PERSON; THEY DO NOT WORK THERE** (D164, PNK-110).
   *
   * A `represented_talent` edge, read from the person's end: the agency is on the list because who represents
   * her is hers to see, and it is never a company she acts in — the server sends it `selectable: false`. An
   * actor may have several at once, so this is decided per row. False for every row an older API sends.
   */
  represents: boolean;
  /**
   * 🔴 **WHAT THIS COMPANY DOES** (D57) — `NETWORK_FUNCTIONS` ids, empty until somebody has said.
   *
   * The sidebar reads it (D91): a module that only a talent agency has any use for is offered to a
   * company that claims `talent_agency` and left off the rail of one that does not. It decides what is
   * DRAWN and never what is allowed — see `nav/navFunctions.ts`.
   */
  functions: string[];
  /**
   * 🔴 **THEIR MARK** (D77) — the content hash of the company's logo, or null where there is none.
   *
   * Not a URL, and deliberately: `/organisations/:id/logo` is `auth: true` and a browser sends no
   * bearer for an `<img src>`, so a bare src would 401 silently and leave the monogram behind with no
   * clue why. The hash is what says "there are bytes to fetch" — `fetchOrganisationMark` fetches them
   * and the kit's `useMarks` holds the object URL for as long as the sidebar is open.
   */
  logoSha256: string | null;
  /**
   * 🔴 **WHAT THEIR MARK READS ON** (D139) — `own` · `light` · `dark` · `either`, measured by the
   * backend from the logo's pixels, or null before it has looked. The badge puts the mark on the plate
   * this asks for: a black wordmark in the dark sidebar was an invisible logo.
   */
  logoBackground: LogoBackground | null;
  /**
   * 🔴 **THEIR COLOURS** (PNK-109) — up to six, most prominent first, read off the company's own
   * website by the research engine and accepted by a person. Empty is the ordinary case.
   *
   * The switcher is the highest-frequency company name in the product — top of the sidebar, on every
   * route, all day — and twelve rows drawn in the tenant's one colour are twelve identical tiles. A
   * producer with three companies switches between them constantly; their own accent is the
   * difference between reading a list and recognising a row.
   *
   * 🔴 It colours a BADGE and nothing else (D28): the tenant row is what skins the Desk, and a
   * company's palette that reached `:root` would turn the whole product green. Nothing sorts, filters
   * or scores on it either (D15) — it is a picture a human looks at.
   */
  brandColours: string[];
  /**
   * 🔴 **THEIR TYPEFACE** (D164 rule 5) — a family name the company badge offers the browser for the name.
   * Never fetched (`fontStack`). Null when nobody has read one, or an older API sent none.
   */
  brandFont: string | null;
}

interface MyOrganisationsResponse {
  organisations: OrganisationChoice[];
}

const isChoice = (value: unknown): value is OrganisationChoice => {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Partial<OrganisationChoice>;
  return typeof row.id === "string" && typeof row.name === "string" && typeof row.selectable === "boolean";
};

const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : []);

/**
 * 🔴 A row that arrives without `functions` becomes one with an EMPTY list, rather than a missing
 * field every reader then has to test for. Empty is a real answer — a company that has never opened
 * Network Functions claims nothing — and `navFunctions.ts` decides what that means for the sidebar.
 *
 * The look is filled in on the same terms (PNK-109): no palette and no mark is the ordinary case and
 * not a failure, so both arrive as their empty answer and `companyLook()` reads that as "draw this
 * company in the tenant's colour". Nothing here judges a colour — the kit is the one place that
 * decides what a palette means, so an older API that answers without these fields still lists.
 */
const normalised = (row: OrganisationChoice): OrganisationChoice => ({
  ...row,
  functions: strings(row.functions),
  represents: row.represents === true,
  logoSha256: typeof row.logoSha256 === "string" && row.logoSha256 !== "" ? row.logoSha256 : null,
  // An older API answers without it, and a word this build does not know is not a plate to paint.
  logoBackground: isLogoBackground(row.logoBackground) ? row.logoBackground : null,
  brandColours: strings(row.brandColours),
  brandFont: typeof row.brandFont === "string" && row.brandFont.trim() !== "" ? row.brandFont : null,
});

/**
 * The organisations this person may act in. `[]` for every failure — see the note above.
 *
 * An empty list is a real, correct state and not only an error's fallback: a valid session whose
 * Passport has no user row yet (somebody claimed a Passport before an administrator added them) has
 * nothing to choose from, and there is nothing wrong with them.
 */
export async function fetchMyOrganisations(fetchImpl?: FetchLike, timeoutMs = ORGANISATIONS_TIMEOUT_MS): Promise<OrganisationChoice[]> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await authFetch(`${API_BASE_URL}${MY_ORGANISATIONS_PATH}`, { signal: abort.signal }, fetchImpl);
    if (!response.ok) return [];
    const body = (await response.json()) as MyOrganisationsResponse;
    if (!Array.isArray(body?.organisations)) return [];
    return body.organisations.filter(isChoice).map(normalised);
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/** A company's own mark — `services/organisation/mark-api.ts`, authenticated like every other read. */
export const organisationMarkPath = (organisationId: string): string => `/organisations/${encodeURIComponent(organisationId)}/logo`;

/**
 * The bytes of one company's mark.
 *
 * 🔴 **FETCHED, NEVER AN `<img src>`** — the same rule `auth/profile.ts` states for a person's photo,
 * for the same reason: the endpoint is authenticated and an `<img>` sends no Authorization header, so
 * a bare src would 401 silently and leave the monogram behind with nothing to explain it.
 *
 * 🔴 **A refusal THROWS, and that is the contract `useMarks` wants.** The composable reads a rejection
 * as "there is nothing to draw here", settles it once and never asks again — a company whose mark this
 * session may not read must not become a retry loop against an answer that has already been given. A
 * null return would only have to be turned back into one.
 */
export async function fetchOrganisationMark(organisationId: string, fetchImpl?: FetchLike): Promise<Blob> {
  const path = organisationMarkPath(organisationId);
  const response = await authFetch(`${API_BASE_URL}${path}`, undefined, fetchImpl);
  if (!response.ok) throw new Error(`${path} answered ${response.status}`);
  const blob = await response.blob();
  // An empty body is not a mark: an object URL made from it draws a broken image where the monogram
  // was doing its job.
  if (blob.size === 0) throw new Error(`${path} answered no bytes`);
  return blob;
}
